import { useMemo, useState } from 'react'
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { SecurityCategory_InstrumentSubtype } from '../../proto/generated/security'

interface Props {
    subtypes: SecurityCategory_InstrumentSubtype[]
    selectedInstrument?: string
    onChange: (instrument: string | undefined) => void
    /** Same portal host as Symbol Search dialog (needed in fullscreen). */
    menuContainer?: Element | null
    /** Must be above SearchDialog (z-index 13000). */
    menuZIndex?: number
}

/** Width reserved for longest labels ("Preference Share", "Corporate Bond") + chevron. */
const TYPE_FILTER_SLOT_WIDTH = 180

/** TradingView-style "All types ▾" filter — menu anchors under the trigger, not screen-center. */
export default function SecurityInstrumentTypeFilter({
    subtypes,
    selectedInstrument,
    onChange,
    menuContainer,
    menuZIndex = 14000,
}: Props) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const hasSubtypes = subtypes.length > 0

    const label = useMemo(() => {
        if (!selectedInstrument) {
            return 'All types'
        }
        return subtypes.find((s) => s.instrument === selectedInstrument)?.displayName ?? selectedInstrument
    }, [selectedInstrument, subtypes])

    const select = (instrument: string | undefined) => {
        onChange(instrument)
        setAnchorEl(null)
    }

    return (
        <Box
            aria-hidden={!hasSubtypes}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexShrink: 0,
                width: TYPE_FILTER_SLOT_WIDTH,
                minWidth: TYPE_FILTER_SLOT_WIDTH,
                height: 28,
                // Keep the slot when Index/Futures/Options have no subtypes — avoids CLS.
                visibility: hasSubtypes ? 'visible' : 'hidden',
                pointerEvents: hasSubtypes ? 'auto' : 'none',
            }}
        >
            <Button
                size="small"
                disableRipple
                tabIndex={hasSubtypes ? 0 : -1}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '18px !important', opacity: 0.7 }} />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                    if (!hasSubtypes) return
                    setAnchorEl(e.currentTarget)
                }}
                sx={{
                    textTransform: 'none',
                    color: 'text.primary',
                    fontSize: 13,
                    fontWeight: 500,
                    px: 0.5,
                    minWidth: 0,
                    maxWidth: '100%',
                    height: 28,
                    whiteSpace: 'nowrap',
                    '& .MuiButton-endIcon': { flexShrink: 0, ml: 0.25 },
                    '&:hover': { backgroundColor: 'transparent', opacity: 0.85 },
                }}
            >
                {label}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open && hasSubtypes}
                onClose={() => setAnchorEl(null)}
                container={menuContainer ?? undefined}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                // SearchDialog uses z-index 13000; default Modal (1300) opens behind it.
                sx={{ zIndex: menuZIndex }}
                slotProps={{
                    paper: {
                        elevation: 3,
                        sx: {
                            mt: 0.5,
                            minWidth: Math.max(TYPE_FILTER_SLOT_WIDTH, 200),
                            maxHeight: 320,
                            borderRadius: 1.5,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            zIndex: menuZIndex,
                        },
                    },
                }}
                MenuListProps={{ dense: true, sx: { py: 0.5 } }}
            >
                <MenuItem
                    selected={!selectedInstrument}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => select(undefined)}
                    sx={{ fontSize: 13, py: 0.75, whiteSpace: 'nowrap' }}
                >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                        {!selectedInstrument ? <CheckIcon fontSize="small" /> : null}
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        All types
                    </ListItemText>
                </MenuItem>
                {subtypes.map((subtype) => {
                    const selected = selectedInstrument === subtype.instrument
                    return (
                        <MenuItem
                            key={subtype.instrument}
                            selected={selected}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => select(subtype.instrument)}
                            sx={{ fontSize: 13, py: 0.75, whiteSpace: 'nowrap' }}
                        >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                {selected ? <CheckIcon fontSize="small" /> : null}
                            </ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                {subtype.displayName}
                            </ListItemText>
                        </MenuItem>
                    )
                })}
            </Menu>
        </Box>
    )
}
